import { DateInput } from '../../components/ui/Form';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { VoidDialog } from '../../components/ui/VoidDialog';
import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints';
import { date, money } from '../../utils/format';
import { usePermission } from '../../hooks/usePermission';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import type { SaleSummary } from '../../types';
import { SalesReturnDrawer } from './SalesReturnDrawer';

const STATUS_TONE: Record<string, string> = {
  POSTED: 'text-success',
  CANCELLED: 'text-danger',
  RETURNED: 'text-danger',
  PARTIALLY_RETURNED: 'text-ink-soft',
};

export function SalesListPage() {
  const { can } = usePermission();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [voiding, setVoiding] = useState<SaleSummary | null>(null);
  const [returning, setReturning] = useState<string | null>(null);
  const [voidError, setVoidError] = useState<unknown>(null);
  const client = useQueryClient();
  const toast = useToast();

  const voidSale = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/sales/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast(`${voiding?.doc_no ?? 'Invoice'} voided`, 'Its pieces are back in stock.');
      void client.invalidateQueries({ queryKey: ['sales'] });
      void client.invalidateQueries({ queryKey: ['items'] });
      setVoiding(null);
      setVoidError(null);
    },
    onError: (e) => {
      setVoidError(e);
      setVoiding(null);
    },
  });

  const { data, error, isLoading } = useQuery({
    queryKey: ['sales', from, to],
    queryFn: () => endpoints.sales.list({ from: from || undefined, to: to || undefined }),
  });

  const rows = (data?.data ?? []) as unknown as SaleSummary[];
  const outstanding = rows.reduce((a, r) => a + Number(r.balance_amount), 0);
  // The column appears only when the data carries profit — which the server
  // sends only to a role allowed to see it. A cashier's list simply has no
  // profit column, rather than a blanked-out one.
  const showProfit =
    can('sales.view_profit') && rows.some((r) => r.gross_profit !== null && r.gross_profit !== undefined);
  const totalProfit = rows.reduce((a, r) => a + Number(r.gross_profit ?? 0), 0);

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle={
          rows.length > 0
            ? `${rows.length} shown · ${money(outstanding.toFixed(2))} still owed${
                showProfit ? ` · ${money(totalProfit.toFixed(2))} profit` : ''
              }`
            : undefined
        }
        actions={
          <>
            <DateInput value={from} onChange={(v) => setFrom(v)} allowFuture />
            <DateInput value={to} onChange={(v) => setTo(v)} allowFuture />
          </>
        }
      />
      <ErrorNotice error={voidError ?? error} />
      {isLoading ? <Loading /> : null}

      {!isLoading && rows.length === 0 ? <Empty message="No invoices in that period." /> : null}

      {rows.length > 0 ? (
        <Table
          head={[
            'Date',
            'Invoice',
            'Customer',
            'Branch',
            'Total',
            'Outstanding',
            ...(showProfit ? ['Profit'] : []),
            'Status',
            '',
          ]}
        >
          {rows.map((s) => (
            <tr key={s.id}>
              <td className="td">{date(s.doc_date)}</td>
              <td className="td num">{s.doc_no}</td>
              <td className="td">{s.party_name}</td>
              <td className="td">{s.branch_name}</td>
              <td className="td num text-right">{money(s.grand_total)}</td>
              <td className="td num text-right">
                {Number(s.balance_amount) > 0 ? (
                  <span className="text-danger">{money(s.balance_amount)}</span>
                ) : (
                  <span className="text-ink-soft">—</span>
                )}
              </td>
              {showProfit ? (
                <td className="td num text-right">
                  {s.gross_profit === null || s.gross_profit === undefined ? (
                    <span className="text-ink-faint">—</span>
                  ) : (
                    <span className={Number(s.gross_profit) < 0 ? 'text-danger' : 'text-success'}>
                      {money(s.gross_profit)}
                    </span>
                  )}
                </td>
              ) : null}
              <td className={`td ${STATUS_TONE[s.status] ?? ''}`}>{s.status.replace(/_/g, ' ').toLowerCase()}</td>
              <td className="td">
                <Link className="text-xs underline" to={`/sales/${s.id}/print`}>
                  Print
                </Link>
                {(s.status === 'POSTED' || s.status === 'PARTIALLY_RETURNED') && can('sales_return.post') ? (
                  <button className="ml-3 text-xs underline" onClick={() => setReturning(s.id)}>
                    Return
                  </button>
                ) : null}
                {s.status === 'POSTED' && can('document.void') ? (
                  <button className="ml-3 text-xs text-danger underline" onClick={() => setVoiding(s)}>
                    Void
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {!can('sales.view_profit') && rows.length > 0 ? (
        <p className="mt-3 text-xs text-ink-soft">Cost and margin are not shown for your role.</p>
      ) : null}
      {returning ? <SalesReturnDrawer saleId={returning} onClose={() => setReturning(null)} /> : null}
      <VoidDialog
        docNo={voiding?.doc_no ?? null}
        what="invoice"
        effect="Every piece on it goes back into stock, the sale and its GST come off the books, and any receipt that paid it stays as the customer's credit — in a reversing entry dated today. To take back only some pieces, use Return instead."
        busy={voidSale.isPending}
        onCancel={() => setVoiding(null)}
        onConfirm={(reason) => voiding && voidSale.mutate({ id: voiding.id, reason })}
      />
    </>
  );
}
